import React, {MouseEvent, ReactNode, useContext} from "react";
import {PresenzeGiornataPlusRecord} from "@commons/mapping/PresenzeGiornataPlusRecord";
import {PresenzeGiustificativo} from "@commons/mapping/PresenzeGiustificativo";
import {DateTime} from "luxon";
import css from './dayCellDisclosure.module.css'
import {GestionePlus} from "@commons/services/presenze_calcolo/GestionePlus";
import {Periodo} from "@commons/types";
import {Disclosure} from "@headlessui/react";
import {averageRGB, floatFormat, invertColor, mm2HHmmFormat, timbratureFormat} from "@commons/utils/util";
import {RiepilogoOrePlus} from "@commons/mapping/RiepilogoOrePlus";
import {LavoratorePresenzeRecord} from "@commons/mapping/LavoratorePresenzeRecord";
import {TabellaWFRecord} from "@commons/mapping/TabellaWFRecord";
import {GiustificativoRecord} from "@commons/mapping/GiustificativoRecord";
import classNames from "classnames";
import {PresenzeCausaleWFRecord} from "@commons/mapping/PresenzeCausaleWF";
import {PageType} from "../dashboardWF/wf.dashboard.detail";
import {useAppSelector} from "@hooks/redux-hooks/hooks";
import {selectSitePreferences} from "@commons/core.slice";
import BadgeGiustificativi from "@components/BadgeGiustificativi";
import BadgeCausaliWF from "@components/BadgeCausaliWF";
import {CoppiaTimbrature} from "@commons/mapping/CoppiaTimbrature";
import {IconAlertCircleFilled, IconCircleCheckFilled, IconCircleFilled, IconClock, IconDeviceFloppy, IconLockFilled} from "@tabler/icons-react";
import {TerminaleRecord} from "@commons/mapping/TerminaleRecord";
import {PresenzeContext} from "@commons/hooks/usePresenze";

interface WFDayCellParams {
    // PROPS PRESENZE
    page: PageType,
    vista?: string,
    periodo: Periodo,
    lavoratore: LavoratorePresenzeRecord,
    day: PresenzeGiornataPlusRecord,
    hhPreviste: string,
    descrizioneTurno?: string,
    isGMAvviata?: boolean,
    days4GM?: PresenzeGiornataPlusRecord[],
    handleMouseClick?: (event: MouseEvent, day: PresenzeGiornataPlusRecord) => void,
    elencoGiuEvidenza: string[],
    causaliWF?: PresenzeCausaleWFRecord[],
    tabellaWF?: TabellaWFRecord,
    submitButtonText?: string | ReactNode,

    listaGiustificativi: GiustificativoRecord[],
    listaTerminali: TerminaleRecord[],
    children: ReactNode,
}

export const BadgePresenzeSTD: React.FC<{ className?: string }> = ({className}) => {
    return <span className={classNames(css.badgeSTD, className)}>STD</span>
}

export const DayCellDisclosure: React.FC<WFDayCellParams> = ({
                                                                 page, vista, periodo,
                                                                 lavoratore, day, hhPreviste,
                                                                 descrizioneTurno, isGMAvviata, days4GM,
                                                                 handleMouseClick, listaGiustificativi, listaTerminali,
                                                                 causaliWF, tabellaWF, elencoGiuEvidenza,
                                                                 submitButtonText, children
                                                             }) => {
        const {currentDay: currentDtGiornata, selectGiornata,} = useContext(PresenzeContext);
        const isHired = (day && day.dtGiornata) ? LavoratorePresenzeRecord.isHired(lavoratore, day.dtGiornata) : false;
        const isFired = (day && day.dtGiornata) ? LavoratorePresenzeRecord.isFired(lavoratore, DateTime.fromISO(day.dtGiornata)) : LavoratorePresenzeRecord.isFired(lavoratore);

        const giorno = (day && day.dtGiornata) ? DateTime.fromISO(day.dtGiornata).toFormat('dd') : '??';
        const mese = (day && day.dtGiornata) ? DateTime.fromISO(day.dtGiornata).toFormat('LL') : '??';
        const hasTotali = day ? PresenzeGiornataPlusRecord.isHired(day) : false;

        const hasAnomalia = function () {
            if (day) {
                return day.anomalia !== 0;
            } else return false;
        };

        const hasWarning = function () {
            if (day) {
                if (day.warning === 0) {
                    return false;
                } else {
                    return (day.warning ^ day.warningChecked) !== 0;
                }
            } else {
                return false
            }
        }

        const getBgColorTimbratura = function (codTerminale: string | null, listaTerminali: TerminaleRecord[]) {
            if (listaTerminali.length) {
                if (codTerminale && codTerminale !== "null") {
                    const terminale = listaTerminali.find(terminale => terminale.codTerminale === codTerminale);
                    return terminale ? terminale.bgColor : undefined;
                } else {
                    return undefined;
                }
            }
        };
        const getDescrizioneGiu = function (codGiu: string) {
            const giustificativo = listaGiustificativi.find(giu => giu.codGiu === codGiu);
            return giustificativo && giustificativo.descrizione ? giustificativo.descrizione : '????'
        }

        // TODO: Verificare il test sulle opt!
        const showGiustificativo = function (codGiu: string) {
            const giustificativo = listaGiustificativi.find(giu => giu.codGiu === codGiu);
            return (giustificativo ? (giustificativo.showFlagsWF & 0x01) > 0 : true)
        }

        const setFillColor = function (item: PresenzeGiustificativo, giustType: 'HH' | 'QI') {
            if (giustType === 'HH') {
                return item.idWF > 0 ? '#2ECC71' : (item.idAttestato !== null && item.idAttestato !== '' ? '#ff0000' : undefined)
            }
            if (giustType === 'QI') {
                return item.idWF > 0 ? '#2ECC71' : undefined
            }
        }

        const inEvidenza = function (day: PresenzeGiornataPlusRecord) {
            const inEvid = false;
            let bgColor: string | null | undefined = '';
            let isMixedColor = false;

            if (day && day.causaliHH !== null) {
                const getColor = function (causale: PresenzeGiustificativo, giuInEvidenza: string) {
                    if (PresenzeGiustificativo.isValid(causale)) {
                        if (causale.codGiu === giuInEvidenza) {
                            const giustificativo = listaGiustificativi.find(giu => giu.codGiu === causale.codGiu)!;
                            if (bgColor === '') {
                                bgColor = GiustificativoRecord.getBgColor(giustificativo);
                            } else {
                                isMixedColor = true;
                                bgColor = averageRGB(bgColor, GiustificativoRecord.getBgColor(giustificativo));
                            }
                        }
                    }
                }

                for (let ind0 = 0; !inEvid && ind0 < elencoGiuEvidenza.length; ++ind0) {
                    const giuInEvidenza = elencoGiuEvidenza[ind0];
                    for (let indGiu = 0; !inEvid && indGiu < day.causaliHH.length; ++indGiu) {
                        let causale = day.causaliHH[indGiu];
                        getColor(causale!, giuInEvidenza)
                    }
                    for (let indGiu = 0; !inEvid && indGiu < day.causaliQI.length; ++indGiu) {
                        let causale = day.causaliQI[indGiu];
                        getColor(causale!, giuInEvidenza)
                    }
                }
            }

            if (bgColor !== '') {
                return {
                    'background-color': isMixedColor ? bgColor + "99" : bgColor,
                    'color': invertColor(bgColor, true)
                };
            } else {
                return {};
            }
        };

        const open = day ? day.dtGiornata === currentDtGiornata : false;

        const weekend = day ? day.giorno6 || day.giorno7 : false;
        const festivo = day ? day.festivo : false;
        const prevOrNextMonth = day ? ((day.mese < periodo.mese) || (day.mese > periodo.mese)) : false;

        const checkXWF = (page === "WF" && tabellaWF);

        const presenzeOpts = useAppSelector(selectSitePreferences).presenzeOptions;

        const presenzeCalendario = vista === 'calendario' && page === 'Presenze' //page === 'Presenze' && presenzeOpts.dayView === 'calendario';

        const showDescrizioneTurno = page === 'Presenze' && (presenzeOpts && presenzeOpts.mostraDescrizioneTurno && vista === 'lista') //&& presenzeOpts.dayView === 'lista'

        const checkTotali = !hasTotali || !isHired || isFired;

        const dayTypeLista = ((weekend && festivo) || festivo) ? (css.dayFestivo + ' ' + css.bgFestivo) : (weekend ? css.bgHeaderWeekend : '');
        const dayTypeCalendario = `${css.dayFestivo} ${css.bgFestivo} border-b-[#E25757]`;
        const WFPrevOrFollowMonth = (page === "WF" && prevOrNextMonth ? `${css.dayPrevOrNextMonth} pointer-events-none` : ''); //SOLO nella pagina WF non è possible lavorare con i giorni a cavallo!
        const hiredFiredStyle = (checkTotali ? 'pointer-events-none text-gray-400' : '');

        const disclosureOpen = (open ? 'border rounded-md !border-[#218a9e]' : '');

        const toHighlight = inEvidenza(day)["background-color"] && inEvidenza(day)["color"];
        const backgroundEvidenza = inEvidenza(day)["background-color"];
        const colorEvidenza = inEvidenza(day)["color"];

        const iconStyle = presenzeCalendario ? 'size-4' : 'size-[18px]';
        const hideLock = ((!day.giornataBloccata && !presenzeCalendario) ? 'invisible' : 'hidden');

        const giornataDetail = <>
            <div className={`font-bold ${presenzeCalendario ? '' : 'inline-flex space-x-0.5'}`}>
                <span><span className={presenzeCalendario ? 'md:text-lg xl:text-xl 2xl:text-2xl' : 'text-lg md:text-xl 2xl:text-2xl'}>{giorno}</span>&#47;<span>{mese}</span></span>
            </div>
            <div className={classNames(css.flexRow, !presenzeCalendario ? (showDescrizioneTurno ? 'w-28 2xl:w-40 gap-x-[1.5px]' : 'w-14 gap-x-[0.5px]') : '')}>
                {/* STATO GIORNATA */}
                {!hasAnomalia() && <IconCircleCheckFilled className={classNames('grow-0 shrink-0 2xl:size-5', hasWarning() ? 'text-warning' : 'text-ggQuadrata', iconStyle)}/>}
                {hasAnomalia() && <IconAlertCircleFilled className={classNames('grow-0 shrink-0 2xl:size-5', day.anomalia === -3 ? 'text-daCalcolare' : 'text-anomalia', iconStyle)}/>}

                {/* LUCCHETTO GIORNATA */}
                {page === 'Presenze' &&
                    <IconLockFilled className={classNames('grow-0 shrink-0 2xl:size-5 text-warning ', day.giornataBloccata ? 'visible' : hideLock, iconStyle)}/>}

                {/* DISCHETTO DI SALVATAGGIO */}
                {(page === 'Presenze' && !presenzeCalendario) &&
                    <IconDeviceFloppy className={`grow-0 shrink-0 size-[18px] 2xl:size-5 my-auto ${day.modified ? 'visible' : 'invisible'}`}/>}

                {/* DESCRIZIONE TURNO */}
                {showDescrizioneTurno && <span className="text-[10px] 2xl:text-[11px] text-left whitespace-nowrap truncate w-20 2xl:w-32 ">{descrizioneTurno}</span>}
            </div>
        </>

        const orariGiornata = <div className={`h-full items-center inline-flex text-xs w-full mx-1 ${presenzeCalendario ? '!text-[11px] md:!text-xs' : ''}`}>
            {isHired && <>
                {(page === 'Presenze' || (checkXWF && (tabellaWF.showOreLavorate || tabellaWF.showOrePreviste))) && <><IconClock className="size-2.5 md:size-3"/>&nbsp;</>}
                {(page === 'Presenze' || (checkXWF && (tabellaWF.showOreLavorate && tabellaWF.showOrePreviste))) && `${mm2HHmmFormat(RiepilogoOrePlus.getTotaleHLav(day.totali))}/${hhPreviste}`}
                {(checkXWF && (tabellaWF.showOreLavorate && !tabellaWF.showOrePreviste)) && mm2HHmmFormat(RiepilogoOrePlus.getTotaleHLav(day.totali))}
                {(checkXWF && (!tabellaWF.showOreLavorate && tabellaWF.showOrePreviste)) && hhPreviste}
            </>}
        </div>

        const hasHHMancanti = (day && day.totali) ? RiepilogoOrePlus.hasMancante(day.totali) : false;
        const hasHHEccedenti = (day && day.totali) ? RiepilogoOrePlus.hasEccedente(day.totali) : false;

        const isSelected4GM = days4GM ? days4GM.find(dayGM => dayGM.dtGiornata === day.dtGiornata) : false;

        return (
            <Disclosure key={day.dtGiornata}>
                <div className={classNames(disclosureOpen, presenzeCalendario ? 'h-full' : '')}>
                    <Disclosure.Button
                        className={classNames('bg-white', css.dayContainer, presenzeCalendario ? 'h-full' : (`min-h-[38px] max-h-fit p-1 ${dayTypeLista} ${hiredFiredStyle}`),
                            WFPrevOrFollowMonth, festivo ? '!border-[#e25757]' : '', (isGMAvviata && isSelected4GM) ? '!bg-[#FFFF00]' : '')}
                        style={toHighlight ? {backgroundColor: `${backgroundEvidenza}`, color: `${colorEvidenza}`} : {}}
                        onClick={(event: any) => {
                            if (day) {
                                if (!checkTotali && !isGMAvviata) {
                                    selectGiornata(open ? null : day);
                                }
                                if (!checkTotali && isGMAvviata && handleMouseClick) {
                                    handleMouseClick(event, day);
                                }
                            }
                        }}

                        onKeyDownCapture={(event: any) => {
                            if (!checkTotali && !isGMAvviata) {
                                event.key === 'Escape' && selectGiornata(null);
                            }
                        }}>

                        {!checkTotali &&
                            <div className={classNames(presenzeCalendario ? (`h-full rounded-md ${weekend ? css.bgWeekend : ''}`) : 'grid gap-y-0.5 md:inline-flex w-full')}>

                                {/* GESTIONE PLUS CON VISTA CALENDARIO */}
                                {presenzeCalendario && <>
                                    <div className={classNames(css.headDayPanel, festivo ? dayTypeCalendario : 'border-b-gray-300', (weekend) ? css.bgHeaderWeekend : '')}>
                                        {giornataDetail}
                                    </div>

                                    {/*ORARIO LAVORATO E PREVISTO DELLA GIORNATA*/}
                                    <div className="px-1 pt-1">
                                        {orariGiornata}
                                    </div>

                                    <div className="px-1 md:px-2 mt-1.5 flex flex-col md:flex-row md:justify-between space-y-0.5 md:space-y-0">
                                        {/* BADGES MANCANTE / ECCEDENTE GIORNATA */}
                                        <div className="inline-flex items-center space-x-0.5 h-3 md:h-3.5">
                                            {hasHHMancanti && <IconCircleFilled className="text-hhMancanti size-3 md:size-3.5"/>}
                                            {hasHHEccedenti && <IconCircleFilled className="text-hhEccedenti size-3 md:size-3.5"/>}
                                        </div>

                                        {/* TIPO DI RILEVAZIONE GIORNATA + DISCHETTO SALVATAGGIO */}
                                        <div className="inline-flex float-end space-x-0.5 items-center h-4 md:h-5 w-fit ml-auto md:ml-0">
                                            {day && day.tipoRilevazione === 'MANUALE' && <BadgePresenzeSTD/>}
                                            {day && day.modified && <IconDeviceFloppy className="size-4 md:size-5"/>}
                                        </div>
                                    </div>
                                </>}

                                {/* GESTIONE PLUS CON VISTA LISTA / WF */}
                                {!presenzeCalendario && <>
                                    <div className={`gap-x-1 ${css.flexRow}`}>
                                        {giornataDetail}
                                        {/*Orario lavorato e previsto della giornata*/}
                                        {orariGiornata}
                                    </div>

                                    {/* --- TIMBRATURE DELLA GIORNATA --- */}
                                    <div className={css.rowTimbrature}>
                                        {/*<div className={`flex flex-row items-center text-sm min-w-[170px] sm:min-w-[230px] 2xl:w-[420px] justify-stretch space-x-1`}>*/}
                                        {isHired && ((checkXWF && tabellaWF.showTimbrature) || page === 'Presenze') && day.effettive.map((item, index) => {
                                            const entrata = CoppiaTimbrature.getEntrata(day.effettive[index]);
                                            const uscita = CoppiaTimbrature.getUscita(day.effettive[index]);

                                            return <span key={index} className="space-x-1">
                                                        <span
                                                            style={{backgroundColor: entrata !== null ? getBgColorTimbratura(day.effettive[index].entrataTerminale, listaTerminali) : '#ffff00'}}
                                                            className="rounded">
                                                            {timbratureFormat(item.entrata)}
                                                        </span>
                                                        <span
                                                            style={{backgroundColor: uscita !== null ? getBgColorTimbratura(day.effettive[index].uscitaTerminale, listaTerminali) : '#ffff00'}}
                                                            className="rounded">
                                                            {timbratureFormat(item.uscita)}
                                                        </span>
                                                    </span>
                                        })}
                                    </div>

                                    {/* --- GIUSTIFICATIVI e CAUSALI DELLA GIORNATA --- */}
                                    <div className={`${css.flexRow} flex-wrap justify-start gap-1 w-full`}>
                                        {/* GIUSTIFICATIVI ORARI */}
                                        {isHired && day.causaliHH[0]!.codGiu !== '' && day.causaliHH.map((causale, index) => {
                                            const item = causale!;
                                            const giustificativoO = listaGiustificativi.find(giu => giu.codGiu === item.codGiu);
                                            const quantitaGiu = parseFloat(hhPreviste) === parseFloat(floatFormat(item.quantita)) ? '' : floatFormat(item.quantita);
                                            const showGiu = page === "WF" ? showGiustificativo(item.codGiu) : true;

                                            if (giustificativoO && showGiu) {
                                                return <BadgeGiustificativi key={item.codGiu + index} label={`${getDescrizioneGiu(item.codGiu)} ${quantitaGiu}`}
                                                                            getGiustificativo={giustificativoO} fillColor={setFillColor(item, 'HH')}/>
                                            }
                                            return null // tolto <></> per problema con "unique key"
                                        })}

                                        {/* GIUSTIFICATIVI QUANTITA'\IMPORTO */}
                                        {isHired && day.causaliQI[0]!.codGiu !== '' && day.causaliQI.map((causale, index) => {
                                            const item = causale!;
                                            const giustificativoQI = listaGiustificativi.find(giu => giu.codGiu === item.codGiu);
                                            const showGiu = page === "WF" ? showGiustificativo(item.codGiu) : true /*TODO: FARE CHECK SU OPT DELLA PAGINA (COME "MOSTRA DISTRIBUZIONE")*/;

                                            const showGiuQI = page === 'Presenze' ? (presenzeOpts && presenzeOpts.mostraGiuQI && vista === 'lista') : true //&& presenzeOpts.dayView === 'lista'

                                            if (giustificativoQI && showGiu && showGiuQI) {
                                                return <BadgeGiustificativi key={item.codGiu + index}
                                                                            label={`${getDescrizioneGiu(item.codGiu)} ${item.quantita !== 0 ? item.quantita : ''}`}
                                                                            getGiustificativo={giustificativoQI} fillColor={setFillColor(item, 'QI')}/>
                                            }
                                            return null  // tolto <></> per problema con "unique key"
                                        })}

                                        {/* CAUSALI WF */}
                                        {/*lavoratore.causaliWF.length > 0 perché quando non c'è nulla in gestione.causaliWF, è una stringa vuota!*/}
                                        {page === "WF" && (isHired && causaliWF && causaliWF.length > 0) && GestionePlus.getCausaliWFGiorno(causaliWF, day).map((causale) => {
                                            return <BadgeCausaliWF key={causale.wf.idWF} label={causale.descrizioneCausale} wfStato={causale.wf.stato!}
                                                                   annullata={causale.wf.annullata}/>
                                        })}
                                    </div>
                                </>}

                            </div>}

                        {/* GESTIONE PLUS CON VISTA LISTA / WF - NO SVILUPPO GIORNI A CAVALLO */}
                        {checkTotali && !presenzeCalendario &&
                            <div className="font-bold text-left">
                                <span className="md:text-lg xl:text-xl">{giorno}</span>&#47;<span>{mese}</span>
                            </div>
                        }

                        {/* GESTIONE PLUS CON VISTA CALENDARIO - NO SVILUPPO GIORNI A CAVALLO */}
                        {(checkTotali && presenzeCalendario) &&
                            <div className={`h-full rounded-md cursor-not-allowed text-gray-400 ${css.bgWeekend}`}>
                                {/* GESTIONE PLUS CON VISTA CALENDARIO */}
                                <div className={classNames(css.headDayPanel, 'border-b-gray-300', css.bgHeaderWeekend)}>
                                    <div className="font-bold">
                                        <span className={presenzeCalendario ? 'md:text-lg xl:text-xl 2xl:text-2xl' : 'text-lg md:text-xl 2xl:text-2xl'}>{giorno}</span>&#47;
                                        <span>{mese}</span>
                                    </div>
                                </div>
                            </div>
                        }
                    </Disclosure.Button>

                    {/* Le classi di Disclosure.Panel servono per la pagina di WF! */}
                    {open && <Disclosure.Panel static className={classNames('rounded-b-lg shadow', page !== 'Presenze' ? 'py-4 px-6' : '')}>
                        {() => (
                            <>
                                {children}

                                {submitButtonText && <div className="flex justify-end mt-5 border-t border-gray-200 pt-5">
                                    <button type="button" onClick={() => selectGiornata(null)}
                                            className="bg-blue-600 btnPrimary">
                                        {submitButtonText}
                                    </button>
                                </div>}
                            </>
                        )}
                    </Disclosure.Panel>}
                </div>
            </Disclosure>
        )
    }
;
